/**
 * SpecialistPanel — редактор специалиста: инструкция, условие вызова, база
 * знаний, файлы, история правок и прогон вопроса.
 *
 * Специалист (08.08) — отдельный агент внутри агента. Основной зовёт его, когда
 * вопрос по его части, получает ответ и отвечает клиенту сам. Поэтому здесь нет
 * каналов и паузы перед ответом: у специалиста нет своего бота, он говорит через
 * основного и живёт по его расписанию.
 *
 * «Когда звать» — не украшение: этот текст уходит в описание инструмента, по
 * нему основной агент и решает, обращаться ли. Пусто — обращаться будет наугад.
 */

import { useEffect, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { History, Loader2, RotateCcw, Save } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { agentsApi } from '@/api/client'
import { AgentKbFiles } from '@/components/AgentKbFiles'
import { KbTopicsEditor, type KbTopic } from '@/components/KbTopicsEditor'

export function SpecialistPanel({ slug }: { slug: string }) {
  const qc = useQueryClient()
  const [tab, setTab] = useState<'instruction' | 'kb' | 'test'>('instruction')
  const [kbPane, setKbPane] = useState<'topics' | 'documents' | 'tables'>('topics')
  const [draft, setDraft] = useState('')
  const [dirty, setDirty] = useState(false)
  const [hint, setHint] = useState('')
  const [hintDirty, setHintDirty] = useState(false)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [showHistory, setShowHistory] = useState(false)
  const [restoring, setRestoring] = useState<string | null>(null)
  const [ask, setAsk] = useState('')
  const [asking, setAsking] = useState(false)
  const [answer, setAnswer] = useState<{ text: string; noData: boolean } | null>(null)

  const { data, isLoading, isError } = useQuery({
    queryKey: ['agent', slug],
    queryFn: () => agentsApi.get(slug),
    staleTime: 30 * 1000,
  })

  // Счётчики на вкладках базы знаний. Ключи те же, что у AgentKbFiles, — данные
  // берутся из общего кэша, лишних запросов нет. Нужны потому, что подключённые
  // таблицы видно только внутри своей вкладки: оператор подключал их и не
  // понимал, попали они куда-нибудь или нет (живой вопрос 08.08).
  const { data: sources } = useQuery({
    queryKey: ['agent-google', slug],
    queryFn: () => agentsApi.googleList(slug),
    staleTime: 30 * 1000,
    enabled: tab === 'kb',
  })
  const { data: files } = useQuery({
    queryKey: ['agent-files', slug],
    queryFn: () => agentsApi.files(slug),
    staleTime: 30 * 1000,
    enabled: tab === 'kb',
  })

  const { data: versions } = useQuery({
    queryKey: ['agent-versions', slug],
    queryFn: () => agentsApi.versions(slug),
    staleTime: 30 * 1000,
    enabled: showHistory,
  })

  const serverPrompt = data?.system_prompt ?? ''
  const serverHint = data?.routing_hint ?? ''
  const topics: KbTopic[] = data?.kb_topics ?? []

  useEffect(() => { if (!dirty) setDraft(serverPrompt) }, [serverPrompt, dirty])
  useEffect(() => { if (!hintDirty) setHint(serverHint) }, [serverHint, hintDirty])
  // Смена специалиста — это другой агент: незакрытые правки прошлого сюда не переносим.
  useEffect(() => { setDirty(false); setHintDirty(false); setAnswer(null); setTab('instruction') }, [slug])

  async function savePrompt() {
    setSaving(true); setError(null)
    try {
      // Инструкцию пишем, только если её правили: каждая запись оставляет след
      // в истории, и правка одного «когда звать» не должна плодить пустые версии.
      if (dirty) await agentsApi.save(slug, { system_prompt: draft })
      if (hintDirty) {
        await agentsApi.updateMeta(slug, { routing_hint: hint })
        setHintDirty(false)
      }
      await qc.invalidateQueries({ queryKey: ['agent', slug] })
      await qc.invalidateQueries({ queryKey: ['subagents'] })
      setDirty(false); setSaved(true)
      setTimeout(() => setSaved(false), 3000)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось сохранить.')
    } finally { setSaving(false) }
  }

  async function persistTopics(next: KbTopic[]) {
    setSaving(true); setError(null)
    try {
      await agentsApi.save(slug, { kb_topics: next })
      await qc.invalidateQueries({ queryKey: ['agent', slug] })
      await qc.invalidateQueries({ queryKey: ['subagents'] })
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось сохранить.')
    } finally { setSaving(false) }
  }

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> Загружаю…
      </div>
    )
  }
  if (isError) {
    return (
      <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
        Не удалось прочитать настройки этого агента.
      </div>
    )
  }

  return (
    <div className="space-y-3">
      <div className="flex w-fit gap-1 rounded-md border p-0.5">
        {([['instruction', 'Инструкция'], ['kb', 'База знаний'], ['test', 'Прогнать вопрос']] as const)
          .map(([k, l]) => (
            <button key={k} type="button" onClick={() => setTab(k)}
                    className={`rounded px-3 py-1 text-xs ${tab === k ? 'bg-accent font-medium' : 'text-muted-foreground'}`}>
              {l}
            </button>
          ))}
      </div>

      {tab === 'instruction' ? (
        <>
          <div className="space-y-1.5">
            <label className="text-xs font-medium">Когда звать этого агента</label>
            <input
              className="w-full rounded-md border bg-background px-3 py-2 text-sm"
              placeholder="Например: вопросы о стоимости, расчёт по площади и материалу"
              value={hint}
              onChange={(e) => { setHint(e.target.value); setHintDirty(true) }}
            />
            <p className="text-[11px] text-muted-foreground">
              По этому описанию основной агент решает, обращаться ли к специалисту.
              Пишите словами клиента, а не названием отдела.
            </p>
          </div>

          <div className="space-y-1.5">
            <label className="text-xs font-medium">Инструкция</label>
            <textarea
              className="min-h-[280px] w-full rounded-md border bg-background px-3 py-2 font-mono text-sm leading-relaxed"
              value={draft}
              onChange={(e) => { setDraft(e.target.value); setDirty(true) }}
              placeholder="Что этот агент умеет и по каким правилам считает или отвечает…"
            />
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3">
            <span className="text-xs text-muted-foreground">
              {draft.length.toLocaleString('ru-RU')} знаков
              {(dirty || hintDirty) && ' · есть несохранённые правки'}
            </span>
            <div className="flex items-center gap-2">
              {saved && <span className="text-xs text-green-700">Сохранено</span>}
              <Button size="sm" className="gap-2" disabled={saving || (!dirty && !hintDirty)} onClick={savePrompt}>
                {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}
                Сохранить
              </Button>
            </div>
          </div>

          <div className="rounded-md border">
            <button
              type="button"
              className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs hover:bg-slate-50"
              onClick={() => setShowHistory((v) => !v)}
            >
              <History className="h-3.5 w-3.5 text-muted-foreground" />
              История правок
              <span className="ml-auto text-muted-foreground">{showHistory ? 'скрыть' : 'показать'}</span>
            </button>
            {showHistory && (
              <div className="divide-y border-t">
                {!versions ? (
                  <div className="flex items-center gap-2 px-3 py-2 text-xs text-muted-foreground">
                    <Loader2 className="h-3 w-3 animate-spin" /> Загружаю…
                  </div>
                ) : versions.length === 0 ? (
                  <p className="px-3 py-3 text-xs text-muted-foreground">
                    Правок ещё не было — история появится после первого сохранения.
                  </p>
                ) : versions.map((v) => (
                  <div key={v.id} className="flex items-center gap-3 px-3 py-2">
                    <div className="min-w-0 flex-1">
                      <p className="text-xs">
                        {new Date(v.created_at).toLocaleString('ru-RU')}
                        {v.author && <span className="text-muted-foreground"> · {v.author}</span>}
                      </p>
                      <p className="truncate text-[11px] text-muted-foreground">
                        инструкция {v.prompt_len.toLocaleString('ru-RU')} знаков · разделов {v.topics}
                      </p>
                    </div>
                    <Button
                      size="sm" variant="outline" className="h-7 gap-1 text-xs"
                      disabled={restoring !== null}
                      onClick={async () => {
                        setRestoring(v.id); setError(null)
                        try {
                          await agentsApi.restore(slug, v.id)
                          setDirty(false)
                          await qc.invalidateQueries({ queryKey: ['agent', slug] })
                          await qc.invalidateQueries({ queryKey: ['agent-versions', slug] })
                        } catch (e) {
                          setError(e instanceof Error ? e.message : 'Не удалось вернуть версию.')
                        } finally { setRestoring(null) }
                      }}
                    >
                      {restoring === v.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <RotateCcw className="h-3 w-3" />}
                      Вернуть
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      ) : tab === 'kb' ? (
        <div className="space-y-3">
          <div className="flex w-fit gap-0.5 rounded border p-0.5 text-xs">
            {([
              ['topics', 'Разделы', topics.length],
              ['documents', 'Документы',
               (files ?? []).filter((f) => f.kind === 'document').length
               + (sources ?? []).filter((d) => d.kind === 'gdoc').length],
              ['tables', 'Таблицы',
               (files ?? []).filter((f) => f.kind === 'table').length
               + (sources ?? []).filter((d) => d.kind === 'gsheet').length],
            ] as const).map(([k, l, n]) => (
              <button key={k} type="button" onClick={() => setKbPane(k)}
                      className={`rounded px-2 py-0.5 ${kbPane === k ? 'bg-accent font-medium' : 'text-muted-foreground'}`}>
                {l}{n > 0 && <span className="ml-1 opacity-60">{n}</span>}
              </button>
            ))}
          </div>
          {kbPane !== 'topics' ? (
            <AgentKbFiles slug={slug} kind={kbPane === 'tables' ? 'table' : 'document'} />
          ) : (
            <KbTopicsEditor
              topics={topics}
              onPersist={persistTopics}
              saving={saving}
              placeholder="Например: Цены по материалам"
              emptyHint="Разделов нет. Специалисту нужны его данные — без них он честно ответит, что данных нет."
            />
          )}
        </div>
      ) : (
        <div className="space-y-3">
          <p className="text-xs text-muted-foreground">
            Специалист отвечает одним ходом и только по своим данным — так же, как
            когда его зовёт основной агент. Спросите его как коллега: с цифрами и
            вводными, без приветствия.
          </p>
          <form
            className="flex gap-2"
            onSubmit={async (e) => {
              e.preventDefault()
              const q = ask.trim()
              if (!q || asking) return
              setAsking(true); setAnswer(null); setError(null)
              try {
                const r = await agentsApi.ask(slug, q)
                setAnswer({ text: r.answer, noData: r.no_data })
              } catch (err) {
                setError(err instanceof Error ? err.message : 'Не удалось прогнать вопрос.')
              } finally { setAsking(false) }
            }}
          >
            <input
              className="flex-1 rounded-md border bg-background px-3 py-2 text-sm"
              placeholder="Матовый потолок 20 м², спальня, Рязань"
              value={ask}
              disabled={asking}
              onChange={(e) => setAsk(e.target.value)}
            />
            <Button type="submit" size="sm" disabled={asking || !ask.trim()}>
              {asking && <Loader2 className="mr-1 h-3 w-3 animate-spin" />}
              Спросить
            </Button>
          </form>
          {answer && (
            <div className={`whitespace-pre-wrap rounded-md border px-3 py-2 text-sm ${
              answer.noData ? 'border-amber-200 bg-amber-50 text-amber-900' : 'bg-slate-50/60'
            }`}>
              {answer.text}
              {answer.noData && (
                <p className="mt-2 text-[11px]">
                  Значит основной агент этого ответа не получит и уведёт разговор
                  к человеку. Добавьте данные в базу знаний специалиста.
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {error && (
        <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
          {error}
        </div>
      )}
    </div>
  )
}
